import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { partyApi, type PartyDetailResponse, type PartyPhone, type PartyAddress, type PartyEmail } from '../services/partyApi';
import { HasContactIndicator } from '../components/HasContactIndicator';
import { useSkipTrace } from '../components/useSkipTrace';
import { SkipTracePanels } from '../components/SkipTracePanels';
import { leadListApi } from '../services/leadListApi';
import { useGoBack } from '../components/useGoBack';
import { PushedIndicator } from '../components/PushedIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { CopyButton } from '../components/CopyButton';
import { SkipTraceInputs, SkipTraceReadyDot } from '../components/SkipTraceInputs';

const entityChipClass = (type: string | null | undefined): string => {
  if (!type) return 'bg-text-faint/15 text-text-faint';
  if (['llc', 'corp', 'partnership'].includes(type)) return 'bg-amber/15 text-amber';
  if (type === 'trust') return 'bg-blue/15 text-blue';
  if (type === 'individual') return 'bg-green/15 text-green';
  if (type === 'government' || type === 'institution') return 'bg-text-faint/15 text-text-dim';
  return 'bg-text-faint/15 text-text-faint';
};

export function PartyDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PartyDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const goBack = useGoBack();
  // Skip-trace agent handoff for this single party (shared hook + panels).
  const skip = useSkipTrace(id ? `party:${id}` : 'party:none');
  function onSkipTrace() {
    if (!data) return;
    const pt = data.party;
    const docNumbers = Array.from(new Set(data.documents.map(d => d.doc_number)));
    skip.submit(pt.display_name_natural ?? pt.display_name, [{
      name: pt.display_name_natural ?? pt.display_name,
      trs: [],
      doc_numbers: docNumbers,
      county: 'Weld',
    }]);
  }

  async function reload() {
    if (!id) return;
    try {
      const r = await partyApi.get(id);
      setData(r);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load');
    }
  }

  useEffect(() => { reload(); }, [id]);

  if (err) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto">
        <button onClick={goBack} className="text-[12px] text-text-faint font-mono hover:text-text-dim">← back</button>
        <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading party…</div>;
  }

  const p = data.party;

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto pb-20">
      <button onClick={goBack} className="text-[12px] text-text-faint font-mono hover:text-text-dim">← back</button>

      <header className="mt-2 flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">party</div>
          <div className="font-display text-[28px] leading-tight flex items-center gap-3">
            <em className="text-accent not-italic">{p.display_name_natural ?? p.display_name}</em>
            <CopyButton text={p.display_name_natural ?? p.display_name}
              title={`Copy "${p.display_name_natural ?? p.display_name}" for TruePeopleSearch`} />
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono space-x-2 flex items-center flex-wrap">
            <span className={'px-2 py-0.5 rounded ' + entityChipClass(p.entity_type)}>{p.entity_type}</span>
            <HasContactIndicator hasPhone={p.has_phone} hasAddress={p.has_address} />
            <span className="text-text-ghost">·</span>
            <span>recorder form: {p.display_name}</span>
            {p.display_suffix && (<><span className="text-text-ghost">·</span><span>suffix: {p.display_suffix}</span></>)}
            {p.last_pushed_to_close_at && (
              <>
                <span className="text-text-ghost">·</span>
                <PushedIndicator pushedAt={p.last_pushed_to_close_at} variant="badge" />
              </>
            )}
            {p.skipped_at && (
              <>
                <span className="text-text-ghost">·</span>
                <SkipMarker skippedAt={p.skipped_at} variant="badge" />
              </>
            )}
          </div>
          <div className="text-[11px] text-text-faint font-mono mt-1">
            {p.first_seen_date && <>first seen {p.first_seen_date}</>}
            {p.last_activity_date && <> · last activity {p.last_activity_date}</>}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <MarkResearchedButton partyId={p.id} pending={data.lead_list_summary.pending} onChanged={reload} />
            <AddToLeadListButton partyId={p.id} data={data} />
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-text-dim cursor-pointer select-none"
              title="Load the skip-trace results into Close when the run finishes">
              <input type="checkbox" checked={skip.loadToClose}
                onChange={e => skip.setLoadToClose(e.target.checked)} className="accent-accent" />
              Load to Close
            </label>
            <button onClick={onSkipTrace} disabled={skip.skipBusy}
              title="Hand this party to the recorder skip-trace agent"
              className="px-3 py-1.5 text-[12px] text-blue border border-blue/40 rounded hover:bg-blue/10 disabled:opacity-50 disabled:cursor-not-allowed">
              {skip.skipBusy ? 'Submitting…' : '→ Skip trace'}
            </button>
          </div>
          <SkipToggleButton partyId={p.id} skippedAt={p.skipped_at} onChanged={reload} />
        </div>
      </header>

      {/* Skip-trace run panels (shared with AOI / lead list). */}
      <SkipTracePanels skip={skip} />

      <section className="mt-6">
        <SkipTraceCard partyId={p.id} initialCity={p.skip_trace_city} initialState={p.skip_trace_state}
          setAt={p.skip_trace_location_set_at} source={p.skip_trace_location_source} onSaved={reload} />
      </section>

      <section className="mt-6 grid grid-cols-3 gap-3">
        <AddressesCard partyId={p.id} addresses={data.addresses} onChanged={reload} />
        <PhonesCard    partyId={p.id} phones={data.phones}       onChanged={reload} />
        <EmailsCard    emails={data.emails} />
        <DetailCard label="Aliases">
          {data.aliases.length === 0 ? (
            <div className="text-[12px] text-text-faint italic">no aliases</div>
          ) : (
            <div className="text-[11px] text-text-dim font-mono space-y-0.5">
              {data.aliases.slice(0, 6).map(a => <div key={a.id}>{a.surface_form}</div>)}
              {data.aliases.length > 6 && <div className="text-text-faint">+ {data.aliases.length - 6} more</div>}
            </div>
          )}
        </DetailCard>
      </section>

      <section className="mt-3">
        <NotesEditor partyId={p.id} initial={p.notes} />
      </section>

      {data.pipeline_pushes.length > 0 && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Pipeline pushes</div>
          <div className="bg-bg-card-soft border border-border rounded">
            {data.pipeline_pushes.map(p => (
              <div key={p.build_batch_id} className="grid grid-cols-[120px_1fr_120px] gap-3 px-4 py-2 border-b border-border last:border-b-0 text-[11px] font-mono">
                <span className="text-text-dim">{p.pushed_at.slice(0,10)}</span>
                <span className="text-text">{p.source_label ?? `${p.source_kind} push`}</span>
                <span className="text-text-faint text-right">{p.pushed_by}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.aois.length > 0 && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Overlapping AOIs</div>
          <div className="flex flex-wrap gap-2">
            {data.aois.map(a => (
              <Link key={a.id} to={`/mi/aoi/${a.id}`}
                className="px-3 py-1.5 text-[12px] border border-border bg-bg-input/40 rounded hover:bg-bg-hover text-text-dim hover:text-text">
                {a.name}{a.basin && <span className="text-text-faint ml-1.5 font-mono text-[10px]">{a.basin}</span>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {data.pool_orders.length > 0 && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Pool orders this party is on</div>
          <div className="bg-bg-card-soft border border-border rounded">
            {data.pool_orders.map(po => (
              <Link key={po.id} to={`/mi/pool-orders/${po.id}`}
                className="block grid grid-cols-[130px_1fr_120px_60px] gap-3 px-4 py-2 border-b border-border last:border-b-0 text-[11px] font-mono hover:bg-bg-hover/40"
                style={{ display: 'grid' }}>
                <span className="text-accent">{po.docket_number ?? po.id.slice(0,8)}</span>
                <span className="text-text-dim">{po.operator ?? '—'}</span>
                <span className="text-text-faint">{po.filed_date ?? '—'}</span>
                <span className="text-text-faint text-right">{po.match_confidence?.toFixed(2) ?? '—'}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {data.leased_owner_rows.length > 0 && (
        <section className="mt-6">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Appendix-A leased rows referencing this party</div>
          <div className="bg-bg-card-soft border border-border rounded">
            {data.leased_owner_rows.map(r => (
              <div key={r.id} className="grid grid-cols-[100px_120px_1fr_120px] gap-3 px-4 py-2 border-b border-border last:border-b-0 text-[11px] font-mono">
                <span className="text-text-dim">{r.lease_date ?? '—'}</span>
                <Link to={`/mi/pool-orders/${r.pool_order_id}`} className="text-accent hover:underline">
                  {r.docket_number ?? r.pool_order_id.slice(0,8)}
                </Link>
                <span className="text-text-dim truncate">{r.pool_operator} ← {r.lessee_name ?? '—'}</span>
                {r.matched_doc_number ? (
                  <Link to={`/mi/document/${r.matched_doc_number}`} className="text-accent hover:underline text-right">
                    {r.matched_doc_number}
                  </Link>
                ) : <span className="text-text-faint text-right">—</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-6">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">
          Last {data.documents.length} document{data.documents.length === 1 ? '' : 's'}
        </div>
        {data.documents.length === 0 ? (
          <div className="bg-bg-card-soft border border-border rounded px-4 py-6 text-[12px] text-text-faint text-center">
            no recorder documents
          </div>
        ) : (
          <div className="bg-bg-card-soft border border-border rounded overflow-hidden">
            <div className="grid grid-cols-[100px_70px_180px_2fr_120px_36px] gap-3 px-4 py-2 bg-bg-input/40 border-b border-border text-[10px] uppercase tracking-wider font-mono text-text-faint">
              <div>Recorded</div>
              <div>Role</div>
              <div>Type</div>
              <div>TRS</div>
              <div className="text-right">Doc #</div>
              <div></div>
            </div>
            {data.documents.map(d => (
              <div key={`${d.doc_number}-${d.role}-${d.ordinal}`}
                className="grid grid-cols-[100px_70px_180px_2fr_120px_36px] gap-3 items-center px-4 py-2 border-b border-border last:border-b-0 text-[11px] font-mono hover:bg-bg-hover/30">
                <span className="text-text-dim">{d.recording_date ?? '—'}</span>
                <span className="text-text-faint">{d.role}</span>
                <div className="min-w-0">
                  <div className="text-text truncate">{d.doc_type ?? '—'}</div>
                  {d.grantee_names && /DEED/i.test(d.doc_type ?? '') && (
                    <div className="text-[10px] text-text-faint truncate" title={`Grantee: ${d.grantee_names}`}>
                      → {d.grantee_names}
                    </div>
                  )}
                </div>
                <span className="text-text-dim truncate">{d.trs_label ?? '—'}</span>
                <Link to={`/mi/document/${d.doc_number}`} className="text-accent hover:underline text-right">
                  {d.doc_number}
                </Link>
                <div className="flex justify-end">
                  <CopyButton text={d.doc_number} title={`Copy ${d.doc_number} to clipboard`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// AddToLeadListButton — header-level affordance to add THIS party to
// the operator's lead list directly from PartyDetail, without going
// back to an AOI or pool order page.
//
// Source binding: lead_list_items requires a (source_kind, source_id).
// Default heuristic from the PartyDetail payload:
//   1. First entry in data.pool_orders[] (sorted by filed_date DESC
//      server-side) — most specific geographic context.
//   2. Else first entry in data.aois[] (overlapping pinned AOIs).
//   3. Else: button is disabled with a tooltip explaining why.
// If the party has multiple candidate sources, a small "▾" affordance
// lets the operator override. Otherwise it's a single-click action.
//
// Operator override semantics: the dropdown picks which source row
// the entry gets bound to. That determines the brief's source line
// at bulk-push time. Operator can always add the same party again
// from a different source page later; the UNIQUE constraint just
// prevents duplicate adds from the SAME source.
function AddToLeadListButton({ partyId, data }: { partyId: string; data: PartyDetailResponse }) {
  type Source = { kind: 'aoi' | 'pool_order'; id: string; label: string };
  const sources: Source[] = [
    ...data.pool_orders.map(po => ({
      kind: 'pool_order' as const,
      id: po.id,
      label: `ECMC ${po.docket_number ?? po.id.slice(0, 8)}${po.operator ? ' · ' + po.operator : ''}`,
    })),
    ...data.aois.map(a => ({
      kind: 'aoi' as const,
      id: a.id,
      label: `${a.name}${a.basin ? ' (' + a.basin + ')' : ''}`,
    })),
  ];

  const [picked, setPicked] = useState<Source | null>(sources[0] ?? null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  async function add(src: Source) {
    setBusy(true);
    setToast(null);
    try {
      const r = await leadListApi.addItems({
        party_ids: [partyId],
        source_kind: src.kind,
        source_id: src.id,
      });
      if (r.added > 0)              setToast(`Added · ${src.label.slice(0, 40)}`);
      else if (r.already_in_list > 0) setToast(`Already on list from this source`);
      else                          setToast('No changes');
    } catch (e: any) {
      setToast(`Failed: ${e?.message ?? 'unknown'}`);
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  if (sources.length === 0) {
    return (
      <button disabled
        title="Add this party to an AOI or pool order roster first; the lead list needs a source for per-party brief context"
        className="px-3 py-2 text-[12px] text-text-faint border border-border rounded cursor-not-allowed">
        + Lead list
      </button>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <button onClick={() => picked && add(picked)} disabled={busy || !picked}
          title={picked ? `Add to lead list with source: ${picked.label}` : 'Pick a source first'}
          className="px-3 py-2 text-[12px] text-text border border-border rounded hover:bg-bg-hover disabled:opacity-50">
          {busy ? 'Adding…' : '+ Lead list'}
        </button>
        {sources.length > 1 && (
          <button onClick={() => setOpen(o => !o)} disabled={busy}
            title="Choose a different source"
            className="px-2 py-2 text-[12px] text-text-faint border border-border border-l-0 rounded hover:text-text disabled:opacity-50">
            ▾
          </button>
        )}
      </div>
      {picked && (
        <div className="mt-1 text-[10px] font-mono text-text-faint text-right truncate max-w-[280px]"
          title={picked.label}>
          source: {picked.label}
        </div>
      )}
      {toast && (
        <div className="absolute top-full mt-2 right-0 px-3 py-1.5 text-[11px] font-mono bg-bg-card border border-border rounded shadow-lg whitespace-nowrap z-10">
          {toast}
        </div>
      )}
      {open && sources.length > 1 && (
        <div className="absolute top-full mt-2 right-0 bg-bg-card border border-border rounded shadow-lg z-10 min-w-[260px]">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-text-faint border-b border-border">
            choose source
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {sources.map(s => (
              <button key={`${s.kind}:${s.id}`}
                onClick={() => { setPicked(s); setOpen(false); }}
                className={
                  'block w-full text-left px-3 py-2 text-[11px] hover:bg-bg-hover ' +
                  (picked && picked.kind === s.kind && picked.id === s.id ? 'text-accent' : 'text-text-dim')
                }>
                <span className="font-mono text-[9px] text-text-faint mr-2 uppercase">{s.kind === 'aoi' ? 'AOI' : 'POOL'}</span>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// MarkResearchedButton — bulk-flips this party's pending lead-list
// items to status='researched' via POST /parties/:id/mark-researched.
// Disabled when the party has 0 pending items (already done, or never
// added to the lead list from any source). Shows the count in the
// label so the operator knows how many sources will move.
function MarkResearchedButton({ partyId, pending, onChanged }: { partyId: string; pending: number; onChanged: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  async function fire() {
    setBusy(true); setErr(null);
    try {
      const r = await partyApi.markResearched(partyId);
      await onChanged();
      if (r.updated === 0) setErr('No pending items to mark');
      setTimeout(() => setErr(null), 2500);
    } catch (e: any) {
      setErr(e?.message ?? 'failed');
    } finally {
      setBusy(false);
    }
  }

  const disabled = pending === 0;
  return (
    <div className="flex flex-col items-end">
      <button onClick={fire} disabled={busy || disabled}
        title={disabled
          ? 'No pending lead-list items for this party — add them to the lead list first'
          : `Flip ${pending} pending lead-list item${pending === 1 ? '' : 's'} for this party to "researched"`}
        className={
          'px-3 py-2 text-[12px] rounded border ' +
          (disabled
            ? 'border-border text-text-faint cursor-not-allowed'
            : 'border-blue/40 bg-blue/10 text-blue hover:bg-blue/20') +
          ' disabled:opacity-50'
        }>
        {busy ? '…' : disabled ? '✓ Mark researched' : `✓ Mark ${pending} researched`}
      </button>
      {err && <div className="mt-1 text-[10px] text-text-faint font-mono">{err}</div>}
    </div>
  );
}

// SkipToggleButton — header affordance to flag a party as "not worth
// pursuing" (or un-flag). PATCH /parties/:id { skipped: bool }
// stamps/clears mi.parties.skipped_at. The red × marker then surfaces
// wherever the party renders.
function SkipToggleButton({ partyId, skippedAt, onChanged }: { partyId: string; skippedAt: string | null; onChanged: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  async function toggle() {
    const nextSkipped = !skippedAt;
    if (nextSkipped) {
      if (!confirm('Mark this party as "not worth pursuing"? A red × will appear wherever their name renders.')) return;
    } else {
      if (!confirm('Un-mark this party? The red × marker will be removed everywhere.')) return;
    }
    setBusy(true); setErr(null);
    try {
      await partyApi.patchSkipped(partyId, nextSkipped);
      await onChanged();
    } catch (e: any) {
      setErr(e?.message ?? 'toggle failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end">
      <button onClick={toggle} disabled={busy}
        title={skippedAt
          ? `Currently marked not worth pursuing (${skippedAt.slice(0, 10)}). Click to un-mark.`
          : 'Flag this party as not worth pursuing. A red × will appear wherever they render.'}
        className={
          'px-3 py-2 text-[12px] rounded border ' +
          (skippedAt
            ? 'border-red/40 bg-red/10 text-red hover:bg-red/20'
            : 'border-border text-text-faint hover:text-red hover:border-red/40') +
          ' disabled:opacity-50'
        }>
        {busy ? '…' : skippedAt ? '× Marked not worth · click to undo' : '× Not worth pursuing'}
      </button>
      {err && <div className="mt-1 text-[10px] text-red font-mono">{err}</div>}
    </div>
  );
}

function DetailCard({ label, children, action }: { label: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-bg-card border border-border rounded p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">{label}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

// PhonesCard — inline editable list. + button adds a row via the
// existing manual-phone path; X per row removes. The most-recent
// is_current=true row floats to the top with a green ● marker.
function PhonesCard({ partyId, phones, onChanged }: { partyId: string; phones: PartyPhone[]; onChanged: () => void | Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [input, setInput]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  async function save() {
    const trimmed = input.trim();
    if (!trimmed) { setAdding(false); setInput(''); return; }
    setBusy(true); setErr(null);
    try {
      await partyApi.addPhone(partyId, trimmed);
      setInput(''); setAdding(false);
      await onChanged();
    } catch (e: any) {
      setErr(e?.message ?? 'add failed');
    } finally { setBusy(false); }
  }

  async function remove(phoneId: string) {
    if (!confirm('Remove this phone number?')) return;
    setBusy(true); setErr(null);
    try {
      await partyApi.deletePhone(partyId, phoneId);
      await onChanged();
    } catch (e: any) {
      setErr(e?.message ?? 'remove failed');
    } finally { setBusy(false); }
  }

  return (
    <DetailCard
      label="Phones"
      action={
        adding ? null : (
          <button onClick={() => setAdding(true)} title="Add phone"
            className="text-[14px] text-accent hover:text-accent-bright leading-none">+</button>
        )
      }
    >
      {phones.length === 0 && !adding && (
        <div className="text-[12px] text-text-faint italic">no phone on file</div>
      )}
      {phones.length > 0 && (
        <div className="space-y-1.5">
          {phones.map(p => (
            <div key={p.id} className="flex items-center justify-between text-[12px]">
              <div className="font-mono">
                {p.is_current && <span className="text-green mr-1.5">●</span>}
                <span className={p.is_current ? 'text-text' : 'text-text-dim'}>{p.phone_e164}</span>
                <span className="text-[10px] text-text-faint ml-2">{p.source}</span>
              </div>
              <button onClick={() => remove(p.id)} disabled={busy} title="Remove"
                className="text-text-faint hover:text-red text-[14px] leading-none disabled:opacity-40">×</button>
            </div>
          ))}
        </div>
      )}
      {adding && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="tel"
            autoFocus
            value={input}
            onChange={e => setInput(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setAdding(false); setInput(''); } }}
            placeholder="(303) 555-0188"
            className="flex-1 bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text"
          />
          <button onClick={() => { setAdding(false); setInput(''); }}
            className="text-[10px] text-text-faint hover:text-text">cancel</button>
        </div>
      )}
      {err && <div className="mt-1 text-[10px] text-red font-mono">{err}</div>}
    </DetailCard>
  );
}

// EmailsCard — read-only. Emails arrive only via Contact Enrich (the CSV
// skip-trace path); there's no manual add/remove flow, so this just
// displays what's on file. is_current row marked with the green dot,
// matching the phones/addresses cards.
function EmailsCard({ emails }: { emails: PartyEmail[] }) {
  return (
    <DetailCard label="Emails">
      {emails.length === 0 ? (
        <div className="text-[12px] text-text-faint italic">no email on file</div>
      ) : (
        <div className="space-y-1.5">
          {emails.map(e => (
            <div key={e.id} className="flex items-center justify-between text-[12px]">
              <div className="font-mono truncate">
                {e.is_current && <span className="text-green mr-1.5">●</span>}
                <span className={e.is_current ? 'text-text' : 'text-text-dim'}>{e.email}</span>
                <span className="text-[10px] text-text-faint ml-2">{e.source}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </DetailCard>
  );
}

// AddressesCard — same shape as phones. Add via expanded multi-field
// form; X removes per row.
function AddressesCard({ partyId, addresses, onChanged }: { partyId: string; addresses: PartyAddress[]; onChanged: () => void | Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [street, setStreet] = useState('');
  const [city, setCity]     = useState('');
  const [state, setState]   = useState('');
  const [postal, setPostal] = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  function cancel() {
    setAdding(false);
    setStreet(''); setCity(''); setState(''); setPostal('');
    setErr(null);
  }

  async function save() {
    if (!street && !city && !state && !postal) { cancel(); return; }
    setBusy(true); setErr(null);
    try {
      await partyApi.addAddress(partyId, { street, city, state, postal });
      cancel();
      await onChanged();
    } catch (e: any) {
      setErr(e?.message ?? 'add failed');
    } finally { setBusy(false); }
  }

  async function remove(addressId: string) {
    if (!confirm('Remove this address?')) return;
    setBusy(true); setErr(null);
    try {
      await partyApi.deleteAddress(partyId, addressId);
      await onChanged();
    } catch (e: any) {
      setErr(e?.message ?? 'remove failed');
    } finally { setBusy(false); }
  }

  return (
    <DetailCard
      label="Addresses"
      action={
        adding ? null : (
          <button onClick={() => setAdding(true)} title="Add address"
            className="text-[14px] text-accent hover:text-accent-bright leading-none">+</button>
        )
      }
    >
      {addresses.length === 0 && !adding && (
        <div className="text-[12px] text-text-faint italic">no address on file</div>
      )}
      {addresses.length > 0 && (
        <div className="space-y-2">
          {addresses.map(a => (
            <div key={a.id} className="flex items-start justify-between text-[12px] gap-2">
              <div className="text-text-dim leading-snug">
                {a.is_current && <span className="text-green mr-1">●</span>}
                {a.street && <span>{a.street}</span>}
                {a.street && (a.city || a.state || a.postal) && <span>, </span>}
                <span>{[a.city, a.state, a.postal].filter(Boolean).join(' ')}</span>
                <div className="text-[10px] text-text-faint font-mono mt-0.5">{a.source}{a.lease_effective_date ? ` · eff ${a.lease_effective_date}` : ''}</div>
              </div>
              <button onClick={() => remove(a.id)} disabled={busy} title="Remove"
                className="shrink-0 text-text-faint hover:text-red text-[14px] leading-none disabled:opacity-40">×</button>
            </div>
          ))}
        </div>
      )}
      {adding && (
        <div className="mt-2 space-y-1.5">
          <input value={street} onChange={e => setStreet(e.target.value)} placeholder="street"
            className="w-full bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text" />
          <div className="grid grid-cols-[1fr_60px_90px] gap-1.5">
            <input value={city} onChange={e => setCity(e.target.value)} placeholder="city"
              className="bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text" />
            <input value={state} onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="ST"
              className="bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text" />
            <input value={postal} onChange={e => setPostal(e.target.value)} placeholder="postal"
              className="bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={cancel} disabled={busy} className="text-[11px] text-text-faint hover:text-text">cancel</button>
            <button onClick={save} disabled={busy} className="text-[11px] text-accent hover:text-accent-bright disabled:opacity-50">save</button>
          </div>
        </div>
      )}
      {err && <div className="mt-1 text-[10px] text-red font-mono">{err}</div>}
    </DetailCard>
  );
}

// NotesEditor — single free-text field on the party. Saves on blur if
// the value changed; trailing whitespace stripped. Empty string saves
// as null so the column is clear in the DB.
function NotesEditor({ partyId, initial }: { partyId: string; initial: string | null }) {
  const [value, setValue]   = useState(initial ?? '');
  const [saved, setSaved]   = useState(initial ?? '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [err, setErr]       = useState<string | null>(null);

  // Re-sync if parent reloads with new initial value.
  useEffect(() => {
    setValue(initial ?? '');
    setSaved(initial ?? '');
  }, [initial, partyId]);

  async function save() {
    const trimmed = value.trimEnd();
    if (trimmed === saved) return;
    setStatus('saving'); setErr(null);
    try {
      const next = trimmed.length === 0 ? null : trimmed;
      await partyApi.patchNotes(partyId, next);
      setSaved(trimmed);
      setStatus('saved');
      setTimeout(() => setStatus(s => s === 'saved' ? 'idle' : s), 1500);
    } catch (e: any) {
      setErr(e?.message ?? 'save failed');
      setStatus('error');
    }
  }

  return (
    <DetailCard
      label="Notes"
      action={
        <span className="text-[10px] font-mono text-text-faint">
          {status === 'saving' && 'saving…'}
          {status === 'saved'  && 'saved ✓'}
          {status === 'error'  && <span className="text-red">{err ?? 'error'}</span>}
        </span>
      }
    >
      <textarea
        value={value}
        onChange={e => { setValue(e.target.value); if (status !== 'idle') setStatus('idle'); }}
        onBlur={save}
        placeholder="Operator notes — saved on blur."
        rows={3}
        className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[12px] text-text-dim placeholder:text-text-ghost resize-y focus:outline-none focus:border-accent/60"
      />
    </DetailCard>
  );
}

// SkipTraceCard — same two-input city/state surface as the LeadList
// row, just rendered as a full-width card. Saves on blur via the same
// partyApi.patchSkipTraceLocation. focusNext is omitted (no "next
// party" semantic on a single-party page) so Enter on the state
// commits and stays put.
function SkipTraceCard(props: {
  partyId: string;
  initialCity: string | null;
  initialState: string | null;
  setAt: string | null;
  source: string | null;
  onSaved: () => void | Promise<void>;
}) {
  const ready = !!(props.initialCity && props.initialState);
  return (
    <div className="bg-bg-card border border-border rounded p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">Skip-trace location</div>
          <SkipTraceReadyDot city={props.initialCity} state={props.initialState} />
        </div>
        <div className="text-[10px] font-mono text-text-faint">
          {ready
            ? `${props.source ?? 'manual'}${props.setAt ? ` · set ${props.setAt.slice(0, 10)}` : ''}`
            : 'capture for EnformionGO Person Search'}
        </div>
      </div>
      <div className="grid grid-cols-[1fr_80px_auto] gap-2 items-center">
        <SkipTraceInputs
          partyId={props.partyId}
          initialCity={props.initialCity}
          initialState={props.initialState}
          onSaved={() => { props.onSaved(); }}
        />
      </div>
    </div>
  );
}
